import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ImportReviewSection } from "@/features/story-import/review/ImportReviewSection";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  acceptStoryImportElement,
  bulkAcceptStoryImportElementType,
  discardStoryImportJob,
  discardStoryImportElement,
  getStoryImportJob,
  readStoryImportReviewBundle,
  readStoryImportSummary,
} from "@/features/story-import/api/storyImportClient";
import { type StoryImportReviewBundle, type StoryImportSummary } from "@/features/story-import/story-import.types";
import {
  buildProjectStoryImportRoute,
  groupStoryImportReviewElements,
} from "@/features/story-import/storyImportMappers";
import {
  buildStoryImportReviewEditState,
  getStoryImportReviewEditHref,
} from "@/features/story-import/review/importReviewEdit";
import { isImportReviewResolved } from "@/features/story-import/review/importReviewGuard";

export function StoryImportReviewPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { importJobId, projectId } = useParams<{ importJobId: string; projectId?: string }>();
  const [bundle, setBundle] = useState<StoryImportReviewBundle | null>(null);
  const [summary, setSummary] = useState<StoryImportSummary | null>(null);
  const [busyElementIds, setBusyElementIds] = useState<string[]>([]);
  const [isDiscardingImport, setIsDiscardingImport] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "error" | "ready">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!importJobId) {
      return;
    }

    let cancelled = false;
    setStatus("loading");
    setErrorMessage(null);

    void Promise.all([
      getStoryImportJob(importJobId),
      readStoryImportSummary(importJobId).catch(() => null),
      readStoryImportReviewBundle(importJobId),
    ])
      .then(([job, loadedSummary, loadedBundle]) => {
        if (cancelled) {
          return;
        }

        if (job.status === "processing" || job.status === "pending") {
          setStatus("error");
          setErrorMessage("storyImport.review.processingState");
          return;
        }

        setBundle(loadedBundle);
        setSummary(loadedSummary);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setStatus("error");
        setErrorMessage("storyImport.review.loadError");
      });

    return () => {
      cancelled = true;
    };
  }, [importJobId]);

  const groups = useMemo(
    () => groupStoryImportReviewElements(bundle?.elements ?? [], bundle?.relationshipSuggestions ?? []),
    [bundle?.elements, bundle?.relationshipSuggestions],
  );
  const canDiscardImport = useMemo(
    () =>
      (bundle?.elements ?? []).every((element) =>
        element.reviewStatus !== "accepted"
        && element.reviewStatus !== "linked"
        && element.reviewStatus !== "merged"
      ),
    [bundle?.elements],
  );

  function withBusyElement(elementId: string, fn: () => Promise<void>) {
    setBusyElementIds((current) => [...current, elementId]);
    return fn()
      .finally(() => {
        setBusyElementIds((current) => current.filter((value) => value !== elementId));
      });
  }

  async function refreshBundle() {
    if (!importJobId) {
      return;
    }

    const nextBundle = await readStoryImportReviewBundle(importJobId);
    setBundle(nextBundle);
  }

  if (!importJobId) {
    return null;
  }

  if (status === "loading") {
    return (
      <Card>
        <CardContent className="p-8">
          <p className="text-base text-muted-foreground">{t("storyImport.review.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  if (status === "error" || !bundle) {
    return (
      <Card>
        <CardContent className="space-y-4 p-8">
          <h1 className="text-3xl font-semibold text-foreground">
            {t("storyImport.review.errorTitle")}
          </h1>
          <p className="text-base leading-7 text-muted-foreground">
            {errorMessage ? t(errorMessage) : t("storyImport.review.loadError")}
          </p>
          <Button asChild type="button" variant="outline">
            <Link to={projectId ? buildProjectStoryImportRoute(projectId) : "/app/story-import"}>
              {t("storyImport.actions.backToImport")}
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[2.25rem] border border-white/70 bg-white/72 p-6 shadow-panel">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-clay">
              {t("storyImport.review.eyebrow")}
            </p>
            <div className="space-y-2">
              <h1 className="font-display text-5xl text-ink">{t("storyImport.review.title")}</h1>
              <p className="max-w-3xl text-base leading-7 text-ink/70">
                {t("storyImport.review.body")}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {canDiscardImport ? (
              <Button
                disabled={isDiscardingImport}
                onClick={() => {
                  setIsDiscardingImport(true);
                  void discardStoryImportJob(importJobId)
                    .then(() => {
                      navigate(buildProjectStoryImportRoute(bundle.projectId), {
                        replace: true,
                      });
                    })
                    .finally(() => {
                      setIsDiscardingImport(false);
                    });
                }}
                type="button"
                variant="outline"
              >
                {t("storyImport.actions.discardImport")}
              </Button>
            ) : null}
            {isImportReviewResolved(bundle.requiredResolution) ? (
              <Button
                onClick={() => navigate(`/projects/${bundle.projectId}`)}
                type="button"
              >
                {t("storyImport.actions.openProject")}
              </Button>
            ) : null}
          </div>
        </div>
      </section>

      {location.state && typeof location.state === "object" && "reviewGuardWarning" in location.state ? (
        <Card className="border-amber-200/70 bg-amber-50/90">
          <CardHeader className="pb-2">
            <h2 className="text-lg font-semibold text-amber-950">
              {t("storyImport.review.guardWarning")}
            </h2>
          </CardHeader>
        </Card>
      ) : null}

      <Card>
        <CardContent className="grid gap-4 p-6 md:grid-cols-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-clay">
              {t("storyImport.review.summary.file")}
            </p>
            <p className="mt-2 text-sm text-foreground">{bundle.sourceFileName}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-clay">
              {t("storyImport.review.summary.language")}
            </p>
            <p className="mt-2 text-sm text-foreground">{bundle.detectedLanguage}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-clay">
              {t("storyImport.review.summary.wordCount")}
            </p>
            <p className="mt-2 text-sm text-foreground">
              {new Intl.NumberFormat().format(
                summary?.estimatedWordCount ?? bundle.documentMetadata.estimatedWordCount,
              )}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-clay">
              {t("storyImport.review.summary.unlockState")}
            </p>
            <p className="mt-2 text-sm text-foreground">
              {isImportReviewResolved(bundle.requiredResolution)
                ? t("storyImport.review.unlocked")
                : t("storyImport.review.locked", {
                    chapters: bundle.requiredResolution.pendingChapterCount,
                    spine: bundle.requiredResolution.pendingStorySpineCount,
                  })}
            </p>
          </div>
        </CardContent>
      </Card>

      {groups.map((group) => (
        <ImportReviewSection
          busyElementIds={busyElementIds}
          group={group}
          key={group.elementType}
          onAccept={(elementId) => {
            void withBusyElement(elementId, async () => {
              await acceptStoryImportElement({
                elementId,
                jobId: importJobId,
              });
              await refreshBundle();
            });
          }}
          onBulkAccept={() => {
            if (!group.bulkAcceptElementType) {
              return;
            }

            void bulkAcceptStoryImportElementType({
              elementType: group.bulkAcceptElementType,
              jobId: importJobId,
            }).then(() => refreshBundle());
          }}
          onDiscard={(elementId) => {
            void withBusyElement(elementId, async () => {
              await discardStoryImportElement({
                elementId,
                jobId: importJobId,
              });
              await refreshBundle();
            });
          }}
          onEdit={(elementId) => {
            const element = bundle.elements.find((candidate) => candidate.id === elementId);
            if (!element) {
              return;
            }

            navigate(getStoryImportReviewEditHref(bundle.projectId, element.elementType), {
              state: buildStoryImportReviewEditState(bundle, element),
            });
          }}
        />
      ))}
    </div>
  );
}
